import { LinearProgress, useTheme, Stack } from "@mui/material";
import { MenuOption } from "components/buttons/TableCellMenuButton";
import { toolbarSearchSx, AUTURA_GRAYSCALE, TEXT_PRIMARY } from "utils";
import {
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ShowHideColumnsButton,
  MRT_TableInstance,
  MRT_RowData,
} from "material-react-table";
import PrintButton from "components/buttons/PrintButton";
import { MoreOptionsButton } from "components/buttons/MoreOptionsButton";
import { TableType } from "hooks/useDefaultTableOptions";

interface MRTableToolbarProps<T extends MRT_RowData> {
  table: MRT_TableInstance<T>;
  menuOptions?: MenuOption[];
  optionsDisabled?: boolean;
  isSubmitting?: boolean;
  children?: React.ReactNode;
  hidden?: boolean;
  hideSearch?: boolean;
  hideFullScreen?: boolean;
  hideColumnFilters?: boolean;
  hideHideShowColumns?: boolean;
  tableType?: TableType;
}

export default function MRTableToolbar<T extends MRT_RowData>({
  table,
  menuOptions = [],
  optionsDisabled = false,
  isSubmitting = false,
  children,
  hideSearch = false,
  hideFullScreen = false,
  hideColumnFilters = false,
  hideHideShowColumns = false,
  hidden = false,
  tableType,
}: MRTableToolbarProps<T>) {
  const { palette } = useTheme();

  const panelTableWrapperSx = {
    display: hidden ? "none" : "block",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    backgroundColor: "transparent",
  };

  const panelTableToolbarSx = {
    width: "100%",
    p: 2,
    pr: 4,
    borderRadius: 0,
    backgroundColor: palette.secondary.main,
    borderBottom: "2px solid",
    borderBottomColor: "primary.main",
    height: 70,
  };

  const nestedTableWrapperSx = {
    display: hidden ? "none" : "block",
    position: "static",
  };

  const nestedTableToolbarSx = {
    width: "100%",
    backgroundColor: AUTURA_GRAYSCALE[200],
    p: 2,
    pr: 4,
    height: 70,
  };

  const buttonColor: string =
    tableType === TableType.NESTED
      ? (TEXT_PRIMARY as string)
      : palette.secondary.contrastText;

  return (
    <Stack
      spacing={0}
      direction={"column"}
      sx={
        tableType === TableType.NESTED
          ? nestedTableWrapperSx
          : panelTableWrapperSx
      }
    >
      <Stack
        spacing={4}
        sx={
          tableType === TableType.NESTED
            ? nestedTableToolbarSx
            : panelTableToolbarSx
        }
        direction={"row"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        {children}
        {!hideSearch && (
          <MRT_GlobalFilterTextField table={table} sx={toolbarSearchSx} />
        )}
        <Stack direction={"row"} spacing={0}>
          <PrintButton
            color={buttonColor}
            hide={!table.getState().isFullScreen}
          />
          {!hideHideShowColumns && (
            <MRT_ShowHideColumnsButton
              table={table}
              sx={{
                color: buttonColor,
                my: "auto",
                "&:hover": { opacity: 0.9 },
              }}
            />
          )}
          {!hideColumnFilters && (
            <MRT_ToggleFiltersButton
              table={table}
              sx={{
                color: buttonColor,
                my: "auto",
                "&:hover": { opacity: 0.9 },
              }}
            />
          )}
          {!hideFullScreen && (
            <MRT_ToggleFullScreenButton
              table={table}
              sx={{
                color: buttonColor,
                my: "auto",
                "&:hover": { opacity: 0.9 },
              }}
            />
          )}
        </Stack>
        {menuOptions.length > 0 && !optionsDisabled && (
          <MoreOptionsButton menuOptions={menuOptions} color={buttonColor} />
        )}
      </Stack>
      <LinearProgress
        color="primary"
        sx={{
          width: "100%",
          height: 4,
          display: isSubmitting ? "block" : "none",
        }}
      />
    </Stack>
  );
}
